import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Person} from '../types/Person';
import peopleService from '../services/peopleService';
import {AdminButton, AdminInputDropdown} from '../../../layout/admin';
import {Button} from 'primereact/button';
import {handleError} from "../../../common/utils/errorHandling";
import {Dialog} from "primereact/dialog";

interface WorkshopMembersProps {
  memberIds: string[];
  onMemberIdsChange: (memberIds: string[]) => void;
}

const WorkshopMembers: React.FC<WorkshopMembersProps> = ({
                                                           memberIds,
                                                           onMemberIdsChange
                                                         }) => {
  const {t} = useTranslation();
  const [people, setPeople] = useState<Person[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');

  useEffect(() => {
    fetchPeople();
  }, []);

  const fetchPeople = async (): Promise<void> => {
    await handleError(
      async () => {
        const peopleData = await peopleService.getAllPeople();
        setPeople(peopleData);
      },
      t('admin.workshops.errors.fetchingPeople')
    );
  };

  const openAddDialog = (): void => {
    setSelectedPersonId('');
    setDialogVisible(true);
  };

  const handleDialogHide = (): void => {
    setDialogVisible(false);
    setSelectedPersonId('');
  };

  const handleAdd = (e: React.FormEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedPersonId) return;

    if (memberIds.includes(selectedPersonId)) {
      alert(t('admin.workshops.members.alreadyMember'));
      return;
    }

    const updatedMemberIds = [...memberIds, selectedPersonId];
    onMemberIdsChange(updatedMemberIds);
    handleDialogHide();
  };

  const handleDelete = (personId: string): void => {
    const updatedMemberIds = memberIds.filter(id => id !== personId);
    onMemberIdsChange(updatedMemberIds);
  };

  const getPersonName = (personId: string): string => {
    const person = people.find(p => p.id === personId);
    return person ? `${person.name} ${person.surname}` : 'Unknown Person';
  };

  const personOptions = people.map(person => ({
    label: `${person.name} ${person.surname}`,
    value: person.id
  }));

  const Member: React.FC<{
    personId: string;
    onDelete: (personId: string) => void;
  }> = ({ personId, onDelete }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded border">
      <div className="flex-1">
        <span className="font-medium">{getPersonName(personId)}</span>
      </div>
      <div className="flex gap-2">
        <Button
          className="hover:text-gray-700"
          type="button"
          icon="fa fa-trash"
          size="small"
          severity="danger"
          outlined
          onClick={() => onDelete(personId)}
        />
      </div>
    </div>
  );

  return (
    <>
      {/* Members List */}
      {memberIds.length > 0 && (
        <div className="mb-4 space-y-2">
          {memberIds.map((personId) => (
            <Member
              key={personId}
              personId={personId}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Add Member Button */}
      <AdminButton
        label={t('admin.workshops.members.addMember')}
        onClick={openAddDialog}
        variant="grey"
      />

      {/* Dialog */}
      <Dialog
        header={t('admin.workshops.members.dialogTitle')}
        visible={dialogVisible}
        style={{width: '50vw'}}
        onHide={handleDialogHide}
        maskClassName="bg-black bg-opacity-50"
      >
        <form onSubmit={handleAdd}>
          <AdminInputDropdown
            label={t('admin.workshops.fields.person')}
            value={selectedPersonId}
            onChange={(e) => setSelectedPersonId(e.target.value)}
            options={personOptions}
            placeholder={t('admin.workshops.placeholders.selectPerson')}
            name="personId"
            id="personId"
            required
            className="mb-4"
          />

          <div className="flex justify-end gap-4 pt-4">
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={handleDialogHide}
              variant="white"
            />
            <AdminButton
              type="submit"
              label={t('admin.common.actions.add')}
              variant="black"
              disabled={!selectedPersonId}
            />
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default WorkshopMembers;


